import React, { useState, useEffect } from "react";
import styles from "./Filters.module.css";
import {
  KeyboardArrowDown as DownIcon,
  KeyboardArrowUp as UpIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

interface FilterCount {
  [key: string]: { [value: string]: number };
}

interface FiltersProps {
  availableFilters: { [key: string]: string[] };
  filters: { [key: string]: string[] };
  filterStyles: { [key: string]: string };
  setFilters: (filters: { [key: string]: string[] }) => void;
  filterCounts: FilterCount;
  currentCategoryLevel?: number;
  initiallyOpen?: boolean;
  isMobile?: boolean; // Add new prop for mobile mode
}

interface CategoryFilter {
  id: number;
  name: string;
  level: number;
  count: number;
  parentId?: number;
  children?: CategoryFilter[];
}

const Filters: React.FC<FiltersProps> = ({
  availableFilters,
  filters,
  filterStyles = {},
  setFilters,
  filterCounts,
  currentCategoryLevel,
  initiallyOpen = false,
  isMobile = false, // Default to desktop mode
}) => {
  // State for mobile filters drawer - use the initiallyOpen prop
  const [isDrawerOpen, setIsDrawerOpen] = useState(initiallyOpen);

  // State for collapsible filter sections
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});

  // Count of active filters for badge display - fix to check if values is an array
  const activeFiltersCount = Object.entries(filters)
    .filter(([_, values]) => Array.isArray(values) && values.length > 0)
    .reduce((count, [_, values]) => count + values.length, 0);

  // Create grouped active filters for quick removal
  const activeFiltersList = Object.entries(filters)
    .filter(([_, values]) => values.length > 0)
    .map(([key, values]) => ({
      key,
      values: values.map((value) => {
        try {
          // Handle JSON-formatted values (like colors or categories) - case-insensitive check
          if (
            key.toLowerCase() === "categorías" ||
            filterStyles[key] === "colorSwatch"
          ) {
            const parsed = JSON.parse(value);
            return {
              displayValue: parsed.name || value,
              originalValue: value,
              id: parsed.id || null,
            };
          }
          return {
            displayValue: value,
            originalValue: value,
            id: null,
          };
        } catch {
          return {
            displayValue: value,
            originalValue: value,
            id: null,
          };
        }
      }),
    }));

  // Initialize collapsible sections - keep Categories expanded, collapse others
  useEffect(() => {
    const initialCollapsedState: { [key: string]: boolean } = {};
    Object.keys(availableFilters).forEach((key) => {
      // Categories are expanded by default, others are collapsed
      initialCollapsedState[key] = key !== "Categorías";
    });
    setCollapsedSections(initialCollapsedState);
  }, [availableFilters]);

  // Use effect to listen for initiallyOpen prop changes
  useEffect(() => {
    setIsDrawerOpen(initiallyOpen);
  }, [initiallyOpen]);

  // Handle filter change with additional tracking
  const handleFilterChange = (
    filterKey: string,
    value: string,
    event: React.ChangeEvent<HTMLInputElement>,
    categoryId?: number
  ) => {
    event.preventDefault();

    if (filterKey === "Categorías" && categoryId) {
      // For category filters, we need to work with JSON objects, not raw IDs
      const currentCategoryFilters = filters[filterKey] || [];

      // Find the complete category object from availableFilters
      const categoryObject = availableFilters[filterKey]?.find(
        (filterValue) => {
          try {
            const parsed = JSON.parse(filterValue);
            return parsed.id === categoryId;
          } catch {
            return false;
          }
        }
      );

      if (!categoryObject) {
        console.warn(`Could not find category object for ID: ${categoryId}`);
        return;
      }

      // Check if this category filter is already selected
      const isSelected = currentCategoryFilters.some((filterValue) => {
        try {
          const parsed = JSON.parse(filterValue);
          return parsed.id === categoryId;
        } catch {
          return false;
        }
      });

      if (isSelected) {
        // Remove the category filter
        const updatedFilters = currentCategoryFilters.filter((filterValue) => {
          try {
            const parsed = JSON.parse(filterValue);
            return parsed.id !== categoryId;
          } catch {
            return true; // Keep non-JSON values (shouldn't happen but safety)
          }
        });
        setFilters({
          ...filters,
          [filterKey]: updatedFilters,
        });
      } else {
        // Add the category filter (it's already a properly formatted JSON string)
        setFilters({
          ...filters,
          [filterKey]: [...currentCategoryFilters, categoryObject],
        });
      }
    } else {
      setFilters({
        ...filters,
        [filterKey]: filters[filterKey]?.includes(value)
          ? filters[filterKey].filter((v) => v !== value)
          : [...(filters[filterKey] || []), value],
      });
    }
  };

  // Handle removing a specific active filter
  const handleRemoveFilter = (filterKey: string, value: string) => {
    setFilters({
      ...filters,
      [filterKey]: filters[filterKey].filter((v) => v !== value),
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({});
  };

  // Toggle a filter section collapse state
  const toggleSection = (filterKey: string) => {
    setCollapsedSections({
      ...collapsedSections,
      [filterKey]: !collapsedSections[filterKey],
    });
  };

  const renderFilterValue = (filterKey: string, value: string) => {
    if (filterStyles[filterKey] === "colorSwatch") {
      try {
        const colorData = JSON.parse(value);
        return (
          <div className={styles.colorSwatch}>
            <div className={styles.colorSwatchInfo}>
              <span
                className={styles.colorDot}
                style={{ backgroundColor: colorData.colorHex }}
                aria-hidden="true"
              />
              <span>{colorData.name}</span>
            </div>
            {/* Count badge will be added by parent component through filterLabel */}
          </div>
        );
      } catch (error) {
        console.error("Error parsing color data:", error);
        return value;
      }
    }
    return value;
  };

  const renderCategoryFilters = (categories: CategoryFilter[]) => {
    return categories.map((category) => (
      <div key={category.id}>
        {category.level === 2 && (
          <label className={styles.filterItem} key={`lvl2-${category.id}`}>
            <input
              type="checkbox"
              checked={filters["Categorías"]?.includes(category.id.toString())}
              onChange={(e) =>
                handleFilterChange("Categorías", category.name, e, category.id)
              }
              aria-label={`Filter by ${category.name}`}
            />
            <span className={styles.filterLabel}>
              {`Ver todo ${category.name}`}
              <span className={styles.filterCount}>({category.count})</span>
            </span>
          </label>
        )}

        {category.level === 3 && (
          <div className={styles.categoryHeader} key={`lvl3-${category.id}`}>
            <h5>{category.name}</h5>
          </div>
        )}

        {category.level === 4 && (
          <label className={styles.filterItem} key={`lvl4-${category.id}`}>
            <input
              type="checkbox"
              checked={filters["Categorías"]?.includes(category.id.toString())}
              onChange={(e) =>
                handleFilterChange("Categorías", category.name, e, category.id)
              }
              aria-label={`Filter by ${category.name}`}
            />
            <span className={styles.filterLabel}>
              {category.name}
              <span className={styles.filterCount}>({category.count})</span>
            </span>
          </label>
        )}

        {category.children && category.children.length > 0 && (
          <div
            className={styles.categoryChildren}
            key={`children-${category.id}`}
          >
            {renderCategoryFilters(category.children)}
          </div>
        )}
      </div>
    ));
  };

  // Modified parseCategoryFilters to maintain parent-child relationships
  const parseCategoryFilters = (values: string[]) => {
    const categories = values
      .map((value) => {
        try {
          const parsed = JSON.parse(value);
          return {
            ...parsed,
            id: Number(parsed.id),
            parentId: parsed.parentId ? Number(parsed.parentId) : undefined,
          };
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    // Filter categories based on current level
    let filteredCategories = categories;
    if (currentCategoryLevel === 2) {
      filteredCategories = categories.filter(
        (cat) => cat.level === 3 || cat.level === 4
      );
    } else if (currentCategoryLevel === 3) {
      filteredCategories = categories.filter((cat) => cat.level === 4);
    }

    return organizeCategories(filteredCategories);
  };

  const organizeCategories = (categories: any[]) => {
    if (currentCategoryLevel === 3) {
      return categories.sort((a, b) => a.name.localeCompare(b.name));
    }

    const level3Categories = categories
      .filter((cat) => cat.level === 3)
      .sort((a, b) => a.name.localeCompare(b.name));

    const result: any[] = [];
    level3Categories.forEach((l3Cat) => {
      result.push(l3Cat);
      const level4Children = categories
        .filter((cat) => cat.level === 4 && cat.parentId === l3Cat.id)
        .sort((a, b) => a.name.localeCompare(b.name));
      result.push(...level4Children);
    });

    return result;
  };

  // Group filters by type
  const groupFilters = () => {
    const groups: { [key: string]: [string, string[]][] } = {
      categories: [],
      attributes: [],
      colors: [],
      others: [],
    };

    console.log("Filters Component Debug:", {
      availableFilterKeys: Object.keys(availableFilters),
      currentCategoryLevel,
      hasCategorias: !!availableFilters["Categorías"],
      categoriasLength: availableFilters["Categorías"]?.length || 0,
      shouldShowCategories:
        currentCategoryLevel === undefined || currentCategoryLevel !== 4,
    });

    // Sort filters to ensure Categorías is first, then organize by type
    Object.entries(availableFilters)
      .filter(([key, _]) => {
        if (key === "Categorías") {
          // Always show categories in search results (when currentCategoryLevel is undefined)
          // Only hide categories when we're at level 4 (deepest category level)
          const shouldShow =
            currentCategoryLevel === undefined || currentCategoryLevel !== 4;
          console.log(
            `Categories filter decision: key=${key}, currentCategoryLevel=${currentCategoryLevel}, shouldShow=${shouldShow}`
          );
          return shouldShow;
        }
        return true;
      })
      .forEach(([key, values]) => {
        if (key === "Categorías") {
          groups.categories.push([key, values]);
        } else if (filterStyles[key] === "colorSwatch") {
          groups.colors.push([key, values]);
        } else if (
          key.includes("Marca") ||
          key.includes("Talla") ||
          key.includes("Tamaño")
        ) {
          groups.attributes.push([key, values]);
        } else {
          groups.others.push([key, values]);
        }
      });

    // Return groups in priority order
    return [
      ...groups.categories,
      ...groups.attributes,
      ...groups.colors,
      ...groups.others,
    ];
  };

  const renderActiveFilters = () => {
    if (activeFiltersCount === 0) return null;

    return (
      <div
        className={styles.activeFilters}
        role="region"
        aria-label="Active filters"
      >
        <div className={styles.activeFiltersHeader}>
          <h4>Filtros activos</h4>
          <button
            onClick={clearAllFilters}
            className={styles.clearAllButton}
            aria-label="Clear all filters"
          >
            <ClearIcon fontSize="small" />
            <span>Borrar todo</span>
          </button>
        </div>
        <div className={styles.activeFiltersList}>
          {activeFiltersList.map(({ key, values }) =>
            values.map(({ displayValue, originalValue }, index) => {
              // Clean display name for category filters
              const displayKey =
                key.toLowerCase() === "categorías" ? "Categoría" : key;

              return (
                <div
                  className={styles.activeFilterItem}
                  key={`${key}-${index}`}
                >
                  <span className={styles.activeFilterName}>{displayKey}:</span>
                  <span className={styles.activeFilterValue}>
                    {displayValue}
                  </span>
                  <button
                    onClick={() => handleRemoveFilter(key, originalValue)}
                    aria-label={`Remove filter ${displayKey}: ${displayValue}`}
                    className={styles.removeFilterButton}
                  >
                    <ClearIcon fontSize="small" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Mobile toggle button for filter drawer
  const renderMobileFilterButton = () => {
    // Only render this button when not in isMobile mode
    if (isMobile) return null;

    return (
      <button
        className={styles.filterToggle}
        onClick={() => setIsDrawerOpen(true)}
        aria-label="Open filters"
        aria-controls="filter-drawer"
        aria-expanded={isDrawerOpen}
      >
        <FilterIcon />
        <span>Filtros</span>
        {activeFiltersCount > 0 && (
          <span className={styles.filterBadge}>{activeFiltersCount}</span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Mobile filter button - only show when not in mobile mode */}
      {!isMobile && (
        <div className={styles.mobileFilterButtonContainer}>
          {renderMobileFilterButton()}
        </div>
      )}

      {/* Filter panel (desktop or mobile drawer) */}
      <div
        className={`${styles.filters} ${
          isDrawerOpen || !isMobile ? styles.open : ""
        } ${isMobile ? styles.mobileView : ""}`}
        id="filter-drawer"
        role="complementary"
        aria-label="Product filters"
      >
        {/* Only show the header when not in mobile mode inside ProductGrid */}
        {!isMobile && (
          <div className={styles.filtersHeader}>
            <h3>Filtros</h3>
            {isDrawerOpen && (
              <button
                className={styles.closeDrawerButton}
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close filters"
              >
                <ClearIcon />
              </button>
            )}
          </div>
        )}

        {/* Display active filters at the top */}
        {renderActiveFilters()}

        {/* Grouped and collapsible filter sections */}
        {groupFilters().map(([key, values]) => (
          <div key={key} className={styles.filterGroup}>
            <button
              className={styles.sectionHeader}
              onClick={() => toggleSection(key)}
              aria-expanded={!collapsedSections[key]}
              aria-controls={`filter-section-${key}`}
            >
              <h4>{key}</h4>
              {collapsedSections[key] ? <DownIcon /> : <UpIcon />}
            </button>

            <div
              id={`filter-section-${key}`}
              className={`${styles.filterGroupContent} ${
                collapsedSections[key] ? styles.collapsed : ""
              }`}
            >
              {key === "Categorías"
                ? renderCategoryFilters(
                    parseCategoryFilters(values as string[])
                  )
                : values.map((value) => (
                    <label
                      key={value}
                      className={styles.filterItem}
                      onClick={(e) => e.preventDefault()}
                    >
                      <input
                        type="checkbox"
                        checked={filters[key]?.includes(value) || false}
                        onChange={(e) => handleFilterChange(key, value, e)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Filter by ${key}: ${
                          typeof value === "string"
                            ? value
                            : JSON.parse(value).name
                        }`}
                      />
                      <span className={styles.filterLabel}>
                        {renderFilterValue(key, value)}
                        <span className={styles.filterCount}>
                          ({filterCounts[key]?.[value] || 0})
                        </span>
                      </span>
                    </label>
                  ))}
            </div>
          </div>
        ))}

        {/* Mobile drawer footer with Apply button - only in mobile mode but not inside ProductGrid */}
        {isMobile && (
          <div className={styles.drawerFooter}>
            <button
              className={styles.applyFiltersButton}
              onClick={() => {
                // Just for standalone mode
              }}
            >
              {activeFiltersCount > 0
                ? `Ver ${activeFiltersCount} filtros aplicados`
                : "Aplicar"}
            </button>
          </div>
        )}
      </div>

      {/* Backdrop for mobile - only show when not in mobile mode and drawer is open */}
      {!isMobile && isDrawerOpen && (
        <div
          className={styles.backdrop}
          onClick={() => setIsDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Filters;
